import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import useSWR, { mutate, useSWRConfig } from 'swr';
import useSWRMutation from 'swr/mutation';

import { hasAsn } from 'src/utils/asn';
import axiosInstance, { API_ENDPOINTS, fetcher } from 'src/utils/axios';

// ----------------------------------------------------------------------

const STUDENTS_KEY = '/api/students';

/**
 * The students visible to the signed-in user. `refreshOnOpen`, for a page
 * that shows the list itself: opened with a list already cached, it shows
 * that list and refreshes it in the background.
 */
export function useGetStudents({ refreshOnOpen = false } = {}) {
  const [lastValidated, setLastValidated] = useState(null);

  // The whole list for the user's schools is heavy (thousands of students, many
  // seconds to build), and the nav and pages ask for it at once. Share one
  // request between them for up to a minute — long enough to cover a slow load,
  // which SWR otherwise stops treating as in flight and requests again — and
  // don't rebuild it each time the window regains focus. Past the minute, the
  // next reader to open refreshes it in the background.
  const { data, isLoading, error, isValidating, mutate } = useSWR(STUDENTS_KEY, fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 60 * 1000,
    onSuccess: () => {
      setLastValidated(new Date());
    },
  });

  // A list page opened again refreshes it, inside the minute too — unless it
  // has nothing cached yet (that first load is already coming) or a load is
  // still running, which the shared cache entry says (a second would only
  // repeat it)
  const { cache } = useSWRConfig();
  const cachedWhenOpened = useRef(data !== undefined);
  useEffect(() => {
    if (refreshOnOpen && cachedWhenOpened.current && !cache.get(STUDENTS_KEY)?.isValidating) mutate();
  }, [refreshOnOpen, cache, mutate]);

  const memoizedValue = useMemo(
    () => ({
      students: data?.data?.students,
      studentsLoading: isLoading,
      studentsError: error,
      studentsValidating: isValidating,
      studentsEmpty: !isLoading && !data?.data?.students?.length,
      lastValidated,
      revalidateStudents: mutate,
    }),
    [data, error, isLoading, isValidating, lastValidated, mutate]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------

export function useGetStudent(studentId, expand) {
  // No request for /student/null or a blank ASN (#583): there is no such student.
  const URL = hasAsn(studentId) ? `${API_ENDPOINTS.student.details(studentId)}` : null;

  const { data, isLoading, error, isValidating } = useSWR(URL, () =>
    fetcher([URL, { params: { expand: expand && expand.join(',') } }])
  );

  const memoizedValue = useMemo(
    () => ({
      student: data?.data?.student,
      studentLoading: isLoading,
      studentError: error,
      studentValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useUpdateStudentAddress(studentId) {
  const URL = `${API_ENDPOINTS.student.details(studentId)}/address`;

  const updateStudentAddress = async (address) => {
    const response = await axiosInstance.put(URL, { ...address });

    return response;
  };

  return updateStudentAddress;
}

// Dry run of useUpdateStudentAddress: the routing request saving this address
// would raise, or `request: null`.
export function usePreviewStudentAddressRouting(studentId) {
  return useCallback(
    async (address) =>
      (await axiosInstance.post(API_ENDPOINTS.student.addressRoutingPreview(studentId), address))
        .data,
    [studentId]
  );
}

export function useUpdateDiplomaChecklist(studentId) {
  const URL = API_ENDPOINTS.student.details(studentId);

  const updateDiplomaChecklist = async (url, { arg }) => {
    const result = await axiosInstance.put(URL, { diplomaChecklist: arg });
    return result.data;
  };

  const { trigger, error, isMutating } = useSWRMutation(URL, updateDiplomaChecklist);

  const memoizedValue = useMemo(
    () => ({
      updateDiplomaChecklist: trigger,
      error,
      isMutating,
    }),
    [trigger, error, isMutating]
  );

  return memoizedValue;
}

export function useUpdateCertificateChecklist(studentId) {
  const URL = API_ENDPOINTS.student.details(studentId);

  const updateCertificateChecklist = async (url, { arg }) => {
    const result = await axiosInstance.put(URL, { certificateChecklist: arg });
    return result.data;
  };

  const { trigger, error, isMutating } = useSWRMutation(URL, updateCertificateChecklist);

  const memoizedValue = useMemo(
    () => ({
      updateCertificateChecklist: trigger,
      error,
      isMutating,
    }),
    [trigger, error, isMutating]
  );

  return memoizedValue;
}

export function useUpdateCareerChecklist(studentId) {
  const URL = API_ENDPOINTS.student.details(studentId);

  const updateCareerChecklist = async (url, { arg }) => {
    const result = await axiosInstance.put(URL, { careerChecklist: arg });
    return result.data;
  };

  const { trigger, error, isMutating } = useSWRMutation(URL, updateCareerChecklist);

  const memoizedValue = useMemo(
    () => ({
      updateCareerChecklist: trigger,
      error,
      isMutating,
    }),
    [trigger, error, isMutating]
  );

  return memoizedValue;
}

export function useUpdateLearningPathway(studentId) {
  const URL = API_ENDPOINTS.student.details(studentId);

  const updateLearningPathway = async (url, { arg }) => {
    const result = await axiosInstance.put(URL, { learningPathway: arg });
    return result.data;
  };

  const { trigger, error, isMutating } = useSWRMutation(URL, updateLearningPathway);

  const memoizedValue = useMemo(
    () => ({
      updateLearningPathway: trigger,
      error,
      isMutating,
    }),
    [trigger, error, isMutating]
  );

  return memoizedValue;
}

export function useUpdateInterests(studentId) {
  const URL = API_ENDPOINTS.student.details(studentId);

  const updateInterests = async (url, { arg }) => {
    const result = await axiosInstance.put(URL, { interests: arg });
    return result.data;
  };

  const { trigger, error, isMutating } = useSWRMutation(URL, updateInterests);

  const memoizedValue = useMemo(
    () => ({
      updateInterests: trigger,
      error,
      isMutating,
    }),
    [trigger, error, isMutating]
  );

  return memoizedValue;
}

export function useUpdateCollegiateInterests(studentId) {
  const URL = API_ENDPOINTS.student.details(studentId);

  const updateCollegiateInterests = async (url, { arg }) => {
    const result = await axiosInstance.put(URL, { collegiateInterests: arg });
    return result.data;
  };

  const { trigger, error, isMutating } = useSWRMutation(URL, updateCollegiateInterests);

  const memoizedValue = useMemo(
    () => ({
      updateCollegiateInterests: trigger,
      error,
      isMutating,
    }),
    [trigger, error, isMutating]
  );

  return memoizedValue;
}

export function useUpdateCourses(studentId) {
  const URL = API_ENDPOINTS.student.details(studentId);

  const updateCourses = async (url, { arg }) => {
    const result = await axiosInstance.put(URL, { courses: arg });
    return result.data;
  };

  const { trigger, error, isMutating } = useSWRMutation(URL, updateCourses);

  const memoizedValue = useMemo(
    () => ({
      updateCourses: trigger,
      error,
      isMutating,
    }),
    [trigger, error, isMutating]
  );

  return memoizedValue;
}

// Mark an off-campus prerequisite (HCS3000 / AGR3000) on or off. The server
// records who marked it, so the cache is patched from its response.
export function useUpdatePrerequisite(studentId) {
  const [isMutating, setIsMutating] = useState(false);

  const updatePrerequisite = useCallback(
    async (code, { completed, dateCompleted }) => {
      setIsMutating(true);
      try {
        const result = await axiosInstance.put(API_ENDPOINTS.student.prerequisite(studentId, code), {
          completed,
          dateCompleted,
        });

        mutate(
          API_ENDPOINTS.student.details(studentId),
          (currentData) => {
            if (!currentData) return currentData;

            return {
              ...currentData,
              data: {
                ...currentData.data,
                student: {
                  ...currentData.data.student,
                  careers: {
                    ...currentData.data.student.careers,
                    prerequisites: result.data.prerequisites,
                  },
                },
              },
            };
          },
          { revalidate: false }
        );

        return result.data;
      } finally {
        setIsMutating(false);
      }
    },
    [studentId]
  );

  return useMemo(() => ({ updatePrerequisite, isMutating }), [updatePrerequisite, isMutating]);
}

export function useGetStudentContacts(studentId) {
  const URL = studentId ? API_ENDPOINTS.student.contacts(studentId) : null;

  const { data, isLoading, error } = useSWR(URL, fetcher);

  return useMemo(
    () => ({
      contacts: data?.contacts || [],
      student: data?.student || null,
      contactsLoading: isLoading,
      contactsError: error,
    }),
    [data, isLoading, error]
  );
}

// The staff career summary. The card re-reads it when the /careers socket says
// it was rewritten, rather than polling.
export function useGetCareerSummary(studentId) {
  const URL = studentId ? API_ENDPOINTS.student.careerSummary(studentId) : null;

  const { data, isLoading, error, mutate } = useSWR(URL, fetcher);

  return useMemo(
    () => ({
      careerSummary: data || null,
      careerSummaryLoading: isLoading,
      careerSummaryError: error,
      refetchCareerSummary: mutate,
    }),
    [data, isLoading, error, mutate]
  );
}

// The worksite industry the student looks suited to (AI-suggested), for staff placing them
export function useGetIndustrySuggestion(asn) {
  const URL = asn ? API_ENDPOINTS.student.industrySuggestion(asn) : null;

  const { data, isLoading, error } = useSWR(URL, fetcher);

  return useMemo(
    () => ({
      industrySuggestion: data || null,
      industrySuggestionLoading: isLoading,
      industrySuggestionError: error,
    }),
    [data, isLoading, error]
  );
}

// Put the list a comment or visit-log write returns into the cached student, so
// the Careers timeline shows the saved items with their ids — an item without
// one can't be edited until a reload fetches it.
function setCachedCareerList(studentId, key, list) {
  if (!Array.isArray(list)) return;
  mutate(
    API_ENDPOINTS.student.details(studentId),
    (currentData) => {
      const student = currentData?.data?.student;
      if (!student) return currentData;
      return {
        ...currentData,
        data: {
          ...currentData.data,
          student: { ...student, careers: { ...student.careers, [key]: list } },
        },
      };
    },
    { revalidate: false }
  );
}

// Put one field a careers write returns (the planned next visit) into the cached student
export function setCachedCareerField(studentId, key, value) {
  mutate(
    API_ENDPOINTS.student.details(studentId),
    (currentData) => {
      const student = currentData?.data?.student;
      if (!student) return currentData;
      return {
        ...currentData,
        data: {
          ...currentData.data,
          student: { ...student, careers: { ...student.careers, [key]: value } },
        },
      };
    },
    { revalidate: false }
  );
}

export function useUpdateComments(studentId) {
  const URL = API_ENDPOINTS.student.comment(studentId);

  const updateComments = async (url, { arg }) => {
    const result = await axiosInstance.post(URL, {
      comment: arg.newComment,
      sendEmail: arg.sendEmail,
      recipients: arg.recipients,
    });
    setCachedCareerList(studentId, 'comments', result.data?.comments);
    return result.data;
  };

  const { trigger, error, isMutating } = useSWRMutation(URL, updateComments);

  const memoizedValue = useMemo(
    () => ({
      updateComments: trigger,
      updateCommentsError: error,
      updateCommentsMutating: isMutating,
    }),
    [trigger, error, isMutating]
  );

  return memoizedValue;
}

export function useEditComment(studentId, commentId) {
  const URL = API_ENDPOINTS.student.editComment(studentId, commentId);

  const editComment = async (url, { arg }) => {
    const result = await axiosInstance.put(URL, { editedComment: arg });
    setCachedCareerList(studentId, 'comments', result.data?.comments);
    return result.data;
  };

  const { trigger, error, isMutating } = useSWRMutation(URL, editComment);


  const memoizedValue = useMemo(
    () => ({
      editComment: trigger,
      editCommentError: error,
      editCommentMutating: isMutating,
    }),
    [trigger, error, isMutating]
  );

  return memoizedValue;
}

export function useUpdateVisitLogs(studentId) {
  const URL = API_ENDPOINTS.student.visitLog(studentId);

  const updateVisitLogs = async (url, { arg }) => {
    // nextVisit (optional): when the counsellor should next see the student (#613);
    // `recipients` (optional) are emailed the visit log, as with a comment
    const { nextVisit, recipients, ...visitLog } = arg;
    const result = await axiosInstance.post(URL, { visitLog, nextVisit, recipients });
    setCachedCareerList(studentId, 'visitLogs', result.data?.visitLogs);
    if (nextVisit) setCachedCareerField(studentId, 'nextVisit', result.data?.nextVisit);
    return result.data;
  };

  const { trigger, error, isMutating } = useSWRMutation(URL, updateVisitLogs);

  const memoizedValue = useMemo(
    () => ({
      updateVisitLogs: trigger,
      updateVisitLogsError: error,
      updateVisitLogsMutating: isMutating,
    }),
    [trigger, error, isMutating]
  );

  return memoizedValue;
}

export function useEditVisitLog(studentId, visitLogId) {
  const URL = API_ENDPOINTS.student.editVisitLog(studentId, visitLogId);

  const editVisitLog = async (url, { arg }) => {
    const result = await axiosInstance.put(URL, { editedVisitLog: arg });
    setCachedCareerList(studentId, 'visitLogs', result.data?.visitLogs);
    return result.data;
  };

  const { trigger, error, isMutating } = useSWRMutation(URL, editVisitLog);


  const memoizedValue = useMemo(
    () => ({
      editVisitLog: trigger,
      editVisitLogError: error,
      editVisitLogMutating: isMutating,
    }),
    [trigger, error, isMutating]
  );

  return memoizedValue;
}

export function useUpdateHighSchoolTrack(studentId) {
  const URL = API_ENDPOINTS.student.details(studentId);

  const updateHighSchoolTrack = async (url, { arg }) => {
    const result = await axiosInstance.put(URL, { highSchoolTrack: arg });
    return result.data;
  };

  const { trigger, error, isMutating } = useSWRMutation(URL, updateHighSchoolTrack);

  const memoizedValue = useMemo(
    () => ({
      updateHighSchoolTrack: trigger,
      error,
      isMutating,
    }),
    [trigger, error, isMutating]
  );

  return memoizedValue;
}

export function useUploadDocuments(studentId) {
  const URL = API_ENDPOINTS.student.documents(studentId);

  const uploadDocuments = async (url, { arg }) => {
    const result = await axiosInstance.put(URL, { documents: arg });
    return result.data;
  };

  const { trigger, error, isMutating } = useSWRMutation(URL, uploadDocuments);

  const memoizedValue = useMemo(
    () => ({
      uploadDocuments: trigger,
      uploadDocumentsError: error,
      uploadDocumentsMutating: isMutating,
    }),
    [trigger, error, isMutating]
  );

  return memoizedValue;
}

export function useDeleteDocument(studentId) {
  const URL = API_ENDPOINTS.student.documents(studentId);

  const deleteDocument = async (url, { arg }) => {
    const result = await axiosInstance.delete(URL, { data: { key: arg } });
    return result.data;
  };

  const { trigger, error, isMutating } = useSWRMutation(URL, deleteDocument);

  const memoizedValue = useMemo(
    () => ({
      deleteDocument: trigger,
      deleteDocumentError: error,
      deleteDocumentMutating: isMutating,
    }),
    [trigger, error, isMutating]
  );

  return memoizedValue;
}

export function useDownloadDocument(studentId) {
  const URL = API_ENDPOINTS.student.documents(studentId);

  const downloadDocument = async (key) => {
    const response = await axiosInstance.get(`${URL}/${key}`);
    return response.data.file;
  };

  return downloadDocument;
}

export function useDownloadCareerProfile(studentId) {
  const URL = API_ENDPOINTS.student.downloadCareerProfile(studentId);

  const downloadCareerProfile = async () => {
    const response = await axiosInstance.get(URL);
    return response.data;
  };

  return downloadCareerProfile;
}

// The list to pick interests from, for editing; { enabled: false } skips it
// (read-only views, and the student's dashboard, which can't reach it)
export function useGetInterests({ enabled = true } = {}) {
  const URL = enabled ? API_ENDPOINTS.student.interestsList : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher);

  const memoizedValue = useMemo(
    () => ({
      interestsList: data,
      interestsListLoading: isLoading,
      interestsListError: error,
      interestsListValidating: isValidating,
      revalidateInterestsList: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}
